import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from '../components/Toast';

/**
 * Toast Context
 * 
 * Provides global toast notification management across the application.
 * Allows components to show success, error, warning, and info messages.
 */
const ToastContext = createContext();

export const useToast = () => {
 const context = useContext(ToastContext);
 if (!context) {
 throw new Error('useToast must be used within a ToastProvider');
 }
 return context;
};

export const ToastProvider = ({ children }) => {
 const [toasts, setToasts] = useState([]);

 const showToast = useCallback((message, type = 'info', duration = 5000) => {
 const id = Date.now() + Math.random();
 const newToast = { id, message, type, duration };
 
 setToasts(prev => [...prev, newToast]);

 // Auto-remove toast after duration
 if (duration > 0) {
 setTimeout(() => {
 removeToast(id);
 }, duration);
 }

 return id;
 }, []);

 const removeToast = useCallback((id) => {
 setToasts(prev => prev.filter(toast => toast.id !== id));
 }, []);

 const showSuccess = useCallback((message, duration) => {
 return showToast(message, 'success', duration);
 }, [showToast]);

 const showError = useCallback((message, duration) => {
 return showToast(message, 'error', duration);
 }, [showToast]);

 const showWarning = useCallback((message, duration) => {
 return showToast(message, 'warning', duration);
 }, [showToast]);

 const showInfo = useCallback((message, duration) => {
 return showToast(message, 'info', duration);
 }, [showToast]);

 const value = {
 showToast,
 showSuccess,
 showError,
 showWarning,
 showInfo,
 removeToast
 };

 return (
 <ToastContext.Provider value={value}>
 {children}
 {/* Render all active toasts */}
 <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-md">
 {toasts.map((toast) => (
 <Toast
 key={toast.id}
 message={toast.message}
 type={toast.type}
 duration={0} // Duration handled by context
 onClose={() => removeToast(toast.id)}
 position="top-right"
 />
 ))}
 </div>
 </ToastContext.Provider>
 );
};

export default ToastContext;
